import { useState, useEffect, useCallback } from 'react';
import axiosClient from '../../../../../../setup/axios/axiosClient';
import { LedgerItem } from '../../../models/LedgerModel';


interface MonthlyUsageResponse {
    items: LedgerItem[];
    currentPage: number;
    totalPages: number;
    totalItems: number;
    limit: number;
}

interface HookParams {
    initialLimit?: number;
}

const useMonthlyUsage = ({ initialLimit = 10 }: HookParams) => {
    const [data, setData] = useState<LedgerItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(initialLimit);

    const calculateTotalCost = (item: LedgerItem): number => {
        return item.baseCost + item.meetingCost + item.recordingCost + item.storageCost;
    };

    const fetchData = useCallback(async (page: number) => {
        setLoading(true);
        setError(null);

        const apiUrl = `/usage/ledger-month-usage?page=${page}&limit=${limit}&sortField=createdAt`;

        try {
            const response = await axiosClient.get<MonthlyUsageResponse>(apiUrl);
            const result = response.data

            const mappedItems = result.items.map(item => ({
                ...item,
                totalCost: calculateTotalCost(item),
            }));

            setData(mappedItems);
            setCurrentPage(result.currentPage);
            setTotalPages(result.totalPages);
            setLimit(result.limit);
        } catch (err) {
            setError('Failed to load monthly usage data.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    // Handlers for UI
    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    function convertYYYYMM(value: string) {
        const str = value.toString()
        const year = str.slice(0, 4)
        const month = Number(str.slice(4)) - 1

        const date = new Date(Number(year), month)
        return date.toLocaleString('en-US', { month: 'long', year: 'numeric' })
    }

    // Initial fetch and dependency on page/limit changes
    useEffect(() => {
        fetchData(currentPage);
    }, [fetchData, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [limit]);


    return {
        data,
        loading,
        error,
        currentPage,
        totalPages,
        limit,
        goToPage,
        convertYYYYMM,
        setLimit
    };
};

export default useMonthlyUsage;